// Reporte de Pago (compacto) para planillas Semanales/Quincenales.
// Muestra únicamente los datos relevantes para el pago del período:
// Empleado · Monto Ext · Salario Bruto · CCSS · Rebajos · Salario Neto.
//
// Se genera en dos formatos: Excel (con estilos, similar al Reporte completo)
// y PDF (una tabla horizontal, con branding Grupo Vértice CR y fuente Unicode que
// renderiza correctamente el símbolo ₡).

import * as XLSX from "xlsx-js-style";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";
import { cuentasSalario, repartirValores, ACTIVIDAD_JORNADAS_ESPECIALES } from "@/lib/payroll";

// Expande filas [nombre, ...valores] en dos filas cuando el trabajador tiene salario dividido.
// netoIdx: índice (dentro de valores) del neto, recalculado en la cuenta 1 como bruto − deducciones.
export function expandirFilasPorCuentas(items, periodEnd, deriveFn) {
  const rows = [];
  const secundarias = new Set();
  for (const { emp, nombre, valores } of items) {
    const cs = cuentasSalario(emp, periodEnd);
    if (!cs) { rows.push([nombre, ...valores]); continue; }
    const [a, b] = repartirValores(valores, cs.pct1, deriveFn);
    rows.push([`${nombre} · ${cs.cuentas[0].nombre}`, ...a]);
    secundarias.add(rows.length);
    rows.push([`${nombre} · ${cs.cuentas[1].nombre}`, ...b]);
  }
  return { rows, secundarias };
}

const COLOR_SECUNDARIA = "F3F0E8";
const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
// Columnas del Reporte de Pago; "Imp. Renta" solo para Servicios con Jornadas especiales.
function layoutPago(empresa) {
  const conRenta = empresa?.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES;
  return conRenta
    ? { conRenta, headers: ["Empleado", "Monto Ext", "Salario Bruto", "CCSS", "Imp. Renta", "Rebajos", "Salario Neto"], widths: [36, 15, 17, 15, 15, 15, 17] }
    : { conRenta, headers: HEADERS, widths: COL_WIDTHS };
}
function filasPago(calculations, periodEnd, conRenta) {
  const items = calculations.map(({ emp, calc }) => ({
    emp, nombre: `${emp.nombre} ${emp.apellidos || ""}`.trim(),
    valores: conRenta
      ? [calc.montoExt, calc.totalBruto, calc.ccss, calc.renta || 0, calc.totalRebajos, calc.neto]
      : [calc.montoExt, calc.totalBruto, calc.ccss, calc.totalRebajos, calc.neto],
  }));
  return expandirFilasPorCuentas(items, periodEnd, (a) => {
    const n = a.length - 1;
    a[n] = r2(a[1] - a.slice(2, n).reduce((x, y) => x + y, 0));
    return a;
  });
}
const totalesPago = (totals, conRenta) => (conRenta
  ? [totals.extras, totals.bruto, totals.ccss, totals.renta || 0, totals.rebajos, totals.neto]
  : [totals.extras, totals.bruto, totals.ccss, totals.rebajos, totals.neto]).map(r2);

const FONT_FAMILY = "NomyProSans";
function registerNomyFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

const COLOR_PRIMARY = "274735";
const COLOR_ACCENT = "D96C4A";
const COLOR_HEADER = "E6EFEA";
const COLOR_TOTAL = "FAF2EE";
const COLOR_TEXT = "1A2421";
const fmtCRC = "#,##0.00";

const styleTitle = {
  font: { name: "Calibri", sz: 16, bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_PRIMARY } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleSubtitle = {
  font: { name: "Calibri", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_ACCENT } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleMetaInfo = {
  font: { name: "Calibri", sz: 10, italic: true, color: { rgb: COLOR_TEXT } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleHeader = {
  font: { name: "Calibri", sz: 10, bold: true, color: { rgb: COLOR_PRIMARY } },
  fill: { fgColor: { rgb: COLOR_HEADER } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "B8C5BE" } },
    bottom: { style: "thin", color: { rgb: "B8C5BE" } },
    left: { style: "thin", color: { rgb: "B8C5BE" } },
    right: { style: "thin", color: { rgb: "B8C5BE" } },
  },
};
const styleCell = {
  font: { name: "Calibri", sz: 10, color: { rgb: COLOR_TEXT } },
  alignment: { vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "EAE7E0" } },
    bottom: { style: "thin", color: { rgb: "EAE7E0" } },
    left: { style: "thin", color: { rgb: "EAE7E0" } },
    right: { style: "thin", color: { rgb: "EAE7E0" } },
  },
};
const styleCellNum = { ...styleCell, alignment: { horizontal: "right", vertical: "center" }, numFmt: fmtCRC };
const styleTotal = {
  font: { name: "Calibri", sz: 11, bold: true, color: { rgb: COLOR_PRIMARY } },
  fill: { fgColor: { rgb: COLOR_TOTAL } },
  alignment: { vertical: "center" },
  border: {
    top: { style: "medium", color: { rgb: COLOR_PRIMARY } },
    bottom: { style: "medium", color: { rgb: COLOR_PRIMARY } },
    left: { style: "thin", color: { rgb: "B8C5BE" } },
    right: { style: "thin", color: { rgb: "B8C5BE" } },
  },
};
const styleTotalNum = { ...styleTotal, alignment: { horizontal: "right", vertical: "center" }, numFmt: fmtCRC };

const HEADERS = ["Empleado", "Monto Ext", "Salario Bruto", "CCSS", "Rebajos", "Salario Neto"];
const COL_WIDTHS = [36, 15, 17, 15, 15, 17];

// -----------------------------------------------------------
// Excel
// -----------------------------------------------------------
export function exportReportePagoExcel({ empresa, calculations, totals, periodoLabel, fileNameLabel, sheetNameLabel, periodEnd }) {
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "";
  const fechaReporte = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  const tituloPeriodo = `Reporte de Pago · ${periodoLabel || ""}`;

  const aoa = [];
  aoa.push([empresaNombre]);
  aoa.push([tituloPeriodo]);
  aoa.push([`Fecha de reporte: ${fechaReporte}`]);
  aoa.push([]);
  const { conRenta, headers: HDR, widths: WID } = layoutPago(empresa);
  aoa.push([...HDR]);

  const { rows: filas, secundarias } = filasPago(calculations, periodEnd, conRenta);
  const firstDataRow = aoa.length;
  aoa.push(...filas);
  const totalRowIdx = aoa.length;
  aoa.push(["TOTAL GENERAL", ...totalesPago(totals, conRenta)]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = WID.map(w => ({ wch: w }));
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 26 };
  ws["!rows"][1] = { hpt: 22 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][4] = { hpt: 24 };
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: HDR.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: HDR.length - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: HDR.length - 1 } },
  ];

  const colTypes = HDR.map((_, i) => (i === 0 ? "s" : "n"));
  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < HDR.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === 4) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) ws[addr].s = colTypes[c] === "n" ? styleTotalNum : styleTotal;
      else if (r > 4) {
        const base = colTypes[c] === "n" ? styleCellNum : styleCell;
        ws[addr].s = secundarias.has(r - firstDataRow) ? { ...base, fill: { fgColor: { rgb: COLOR_SECUNDARIA } } } : base;
      }
    }
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Pago ${sheetNameLabel || ""}`.trim());
  XLSX.writeFile(wb, `${fileNameLabel || "Reporte_de_Pago"}.xlsx`);
}

// -----------------------------------------------------------
// PDF (landscape, jsPDF + autoTable) — mismo estándar visual que el
// Reporte de Excel (banda cabecera moss green + sub-banda terracotta).
// Usa fuente embebida NomyProSans (DejaVu Sans) para renderizar ₡.
// -----------------------------------------------------------
const money = (n) => "\u20A1" + new Intl.NumberFormat("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

export function exportReportePagoPDF({ empresa, calculations, totals, periodoLabel, fileNameLabel, periodEnd }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
  registerNomyFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();

  const empresaNombre = empresa?.razon_social || empresa?.nombre || "";
  const fecha = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });

  // Banda superior — moss green (39, 71, 53) igual que el Excel
  doc.setFillColor(39, 71, 53);
  doc.rect(0, 0, pageW, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(16);
  doc.text(empresaNombre, 12, 11);
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(11);
  doc.text("Reporte de Pago", 12, 18);

  // Sub-banda — terracotta (217, 108, 74) igual que el Excel
  doc.setFillColor(217, 108, 74);
  doc.rect(0, 22, pageW, 8, "F");
  doc.setFontSize(10);
  doc.setFont(FONT_FAMILY, "bold");
  doc.text(periodoLabel || "", 12, 28);
  doc.setFont(FONT_FAMILY, "normal");
  doc.text(`Fecha del reporte: ${fecha}`, pageW - 12, 28, { align: "right" });

  const { conRenta, headers: HDR } = layoutPago(empresa);
  const { rows: filas, secundarias } = filasPago(calculations, periodEnd, conRenta);
  const body = filas.map(r => [r[0], ...r.slice(1).map(money)]);
  const columnStyles = { 0: { cellWidth: 80 } };
  for (let i = 1; i < HDR.length; i++) columnStyles[i] = { halign: "right" };
  columnStyles[HDR.length - 1] = { halign: "right", fontStyle: "bold", textColor: [39, 71, 53] };

  autoTable(doc, {
    startY: 36,
    head: [HDR],
    body,
    foot: [["TOTAL GENERAL", ...totalesPago(totals, conRenta).map(money)]],
    theme: "grid",
    styles: {
      font: FONT_FAMILY, fontStyle: "normal",
      fontSize: 9, cellPadding: 3, textColor: [26, 36, 33],
      lineColor: [230, 227, 220], lineWidth: 0.1,
    },
    headStyles: {
      font: FONT_FAMILY, fontStyle: "bold",
      fillColor: [230, 239, 234], textColor: [39, 71, 53], halign: "center",
      lineColor: [184, 197, 190], lineWidth: 0.2,
    },
    footStyles: {
      font: FONT_FAMILY, fontStyle: "bold",
      fillColor: [250, 242, 238], textColor: [39, 71, 53],
      lineColor: [39, 71, 53], lineWidth: 0.3,
    },
    columnStyles,
    alternateRowStyles: { fillColor: [250, 249, 246] },
    margin: { left: 10, right: 10 },
    didParseCell: (data) => { if (data.section === "body" && secundarias.has(data.row.index)) data.cell.styles.fillColor = [243, 240, 232]; },
  });

  // Footer discreto con marca
  const finalY = doc.lastAutoTable.finalY || 40;
  doc.setFontSize(8);
  doc.setTextColor(150, 145, 140);
  doc.setFont(FONT_FAMILY, "normal");
  doc.text(`Generado por ${empresaNombre}`, 12, finalY + 8);
  doc.text(fecha, pageW - 12, finalY + 8, { align: "right" });

  doc.save(`${fileNameLabel || "Reporte_de_Pago"}.pdf`);
}

// -----------------------------------------------------------
// Reporte tabular genérico (Excel/PDF) con el mismo diseño del Reporte de Pago.
// colTypes: "s" texto · "n" colones · "h" horas
// -----------------------------------------------------------
export function exportReporteTabla({ empresa, titulo, headers, colTypes, widths, rows, totalRow, periodoLabel, fileNameLabel, sheetName, formato, secundarias = new Set() }) {
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "";
  const fecha = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  if (formato === "pdf") {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
    registerNomyFont(doc);
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(39, 71, 53);
    doc.rect(0, 0, pageW, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(FONT_FAMILY, "bold");
    doc.setFontSize(16);
    doc.text(empresaNombre, 12, 11);
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(11);
    doc.text(titulo, 12, 18);
    doc.setFillColor(217, 108, 74);
    doc.rect(0, 22, pageW, 8, "F");
    doc.setFontSize(10);
    doc.setFont(FONT_FAMILY, "bold");
    doc.text(periodoLabel || "", 12, 28);
    doc.setFont(FONT_FAMILY, "normal");
    doc.text(`Fecha del reporte: ${fecha}`, pageW - 12, 28, { align: "right" });
    const fmt = (v, t) => t === "n" ? money(v) : t === "h" ? (v === "" ? "" : `${(Number(v) || 0).toFixed(2)} h`) : String(v ?? "");
    const columnStyles = {};
    colTypes.forEach((t, i) => { if (t !== "s") columnStyles[i] = { halign: "right" }; });
    columnStyles[0] = { cellWidth: 70 };
    columnStyles[colTypes.length - 1] = { halign: "right", fontStyle: "bold", textColor: [39, 71, 53] };
    autoTable(doc, {
      startY: 36,
      head: [headers],
      body: rows.map(r => r.map((v, i) => fmt(v, colTypes[i]))),
      foot: [totalRow.map((v, i) => fmt(v, colTypes[i]))],
      theme: "grid",
      styles: { font: FONT_FAMILY, fontStyle: "normal", fontSize: 9, cellPadding: 3, textColor: [26, 36, 33], lineColor: [230, 227, 220], lineWidth: 0.1 },
      headStyles: { font: FONT_FAMILY, fontStyle: "bold", fillColor: [230, 239, 234], textColor: [39, 71, 53], halign: "center", lineColor: [184, 197, 190], lineWidth: 0.2 },
      footStyles: { font: FONT_FAMILY, fontStyle: "bold", fillColor: [250, 242, 238], textColor: [39, 71, 53], lineColor: [39, 71, 53], lineWidth: 0.3, halign: "right" },
      columnStyles,
      alternateRowStyles: { fillColor: [250, 249, 246] },
      margin: { left: 10, right: 10 },
      didParseCell: (data) => { if (data.section === "body" && secundarias.has(data.row.index)) data.cell.styles.fillColor = [243, 240, 232]; },
    });
    const finalY = doc.lastAutoTable.finalY || 40;
    doc.setFontSize(8);
    doc.setTextColor(150, 145, 140);
    doc.text(`Generado por ${empresaNombre}`, 12, finalY + 8);
    doc.text(fecha, pageW - 12, finalY + 8, { align: "right" });
    doc.save(`${fileNameLabel}.pdf`);
    return;
  }
  const aoa = [[empresaNombre], [`${titulo} · ${periodoLabel || ""}`], [`Fecha de reporte: ${fecha}`], [], [...headers], ...rows];
  const totalRowIdx = aoa.length;
  aoa.push(totalRow);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = widths.map(w => ({ wch: w }));
  ws["!rows"] = [{ hpt: 26 }, { hpt: 22 }, { hpt: 18 }, undefined, { hpt: 24 }];
  ws["!merges"] = [0, 1, 2].map(r => ({ s: { r, c: 0 }, e: { r, c: headers.length - 1 } }));
  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      const num = colTypes[c] !== "s";
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === 4) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) ws[addr].s = num ? styleTotalNum : styleTotal;
      else if (r > 4) {
        const base = num ? styleCellNum : styleCell;
        ws[addr].s = secundarias.has(r - 5) ? { ...base, fill: { fgColor: { rgb: COLOR_SECUNDARIA } } } : base;
      }
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileNameLabel}.xlsx`);
}
